//@flow
import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import classnames from 'classnames'

import * as Modals from './modals'
import { ControllerError } from 'components/common/form'
import { Flex, Icon } from 'components/common'
import { useSoundPlay } from '~/core/hooks'
import { useGlobalDispatch } from '~/core/context'
import { useSoundsSelectStyles } from './styles'
import { useI18n } from '~/core/i18n'

type SoundsWrapperProps = {
  id?: string,
  label?: string,
  placeholder?: string,
  isMulti?: boolean,
  isDisabled?: boolean,
  value?: string[] | string,
  onChange: (sounds: string[] | string) => void
}

const SoundsWrapper = ({
  id,
  isMulti,
  isDisabled,
  label,
  value,
  onChange,
  placeholder,
  ...props
}: SoundsWrapperProps) => {
  const [msg] = useI18n()
  const globalDispatch = useGlobalDispatch()
  const classes = useSoundsSelectStyles()

  const selectedValue = value ? (typeof value === 'string' ? [value] : value) : []
  const isSoundListVisible = Boolean(isMulti && !isDisabled && selectedValue.length)
  const placeholderText = placeholder || msg('pbx.media.sounds.content.selectFiles')

  const handleOpenModal = () =>
    globalDispatch({
      type: 'modal/open',
      payload: {
        title: msg('pbx.media.sounds.content.selectFiles'),
        content: (
          <Modals.SoundsSelect
            isMulti={isMulti}
            value={selectedValue}
            onChange={data => {
              globalDispatch({ type: 'modal/close' })
              onChange(isMulti ? data : data[0])
            }}
          />
        )
      }
    })

  return (
    <Flex className={classes.soundsControlleWrapper} column>
      {label && <label id={id ? `${id}-label` : ''}>{label}</label>}
      <div
        className={classnames(classes.soundsControllerContent, {
          disabled: isDisabled
        })}
      >
        {isSoundListVisible && (
          <Flex className={classes.soundsListWrapper} id={id} {...props} column>
            {selectedValue.map(sound => (
              <Flex key={sound} alignCenter className="selected-sound-item">
                <Icon icon="music" />
                <span>{sound}</span>
              </Flex>
            ))}
          </Flex>
        )}
        <Flex
          alignCenter
          className={classes.soundsSelectButton}
          id={id ? `${id}-select-btn` : ''}
          title={msg('pbx.media.sounds.content.clickToSelect')}
          onClick={handleOpenModal}
        >
          <i className="icon icon-s">upload</i>
          <span>{!isMulti && !isDisabled && value ? value : placeholderText}</span>
        </Flex>
      </div>
    </Flex>
  )
}

type SoundsControlleProps = {
  name: string,
  label?: string,
  rules?: {
    [key: string]: any
  },
  wrapperProps?: {
    [key: string]: any
  }
}

const SoundsController = ({ name, rules, wrapperProps, ...props }: SoundsControlleProps) => {
  const { errors } = useFormContext()
  return (
    <Flex column {...wrapperProps}>
      <Controller
        name={name}
        render={({ field: { value, onChange } }) => <SoundsWrapper value={value} onChange={onChange} {...props} />}
        rules={rules}
      />
      <ControllerError errors={errors} name={name} />
    </Flex>
  )
}

export { SoundsController }
